import throttle from 'lodash/throttle';

/**
 * OBS:
 *      This component consists of two elements. The root element, and a child element with the data-scroll-sticker-elem attribute.
 *      When stickyness is applied, the data-scroll-sticker-elem is position fixed, and therefore pulled out of the layout flow
 *      of the root element.
 *      It is required that the root elements width will change be affected by this, since this is measured
 *      and used to set the width of the data-scroll-sticker-elem.
 *      The height of the root element will be locked to the height it has before the data-scroll-sticker-elem is
 *      set to sticky.
 *
 *      The sticky elem expects the target position to be below the initial position.
 *
 * ARGS:
 *      - target (required) : [DOM element or selector] - Is the target DOM element the scroll sticker should stick to.
 *      - snapToTargetBottom : [Boolean] - If true, it will scroll the bottom of the scroll sticker to the bottom of the target. Defaults to true.
 *      - fixToScreenTop: [Boolean] - If true the scroll sticker will stick when the top of the screen hits the top of the scroll sticker. If false, the stickyness will trigger as soon as the screen scrolls. Defaults to true.
 *      - fixToScreenTopOffset: [Number] - Offsets when the top of the screen will trigger the stick.
 *      - breakpoint: [Number] - If added, the stick will only apply from screensizes equal or above the breakpoint.
 *      - usePositionFixed: [Boolean] - If true, the sticky elem will be position fixed when sticky. If false, it will be position absolute, and requires a position relative on the root element. Defaults to true.
 *      - throttleDelay: [number] - the throttle delay for the on scroll event handler. Defaults to 10.
 *      - saveRootHeight: [Boolean] - If true, then the root element will have its height set, whenever stickyness starts. This is to prevent content below from jumping. default to true.
 *      - relateStickyWidthToRoot: [Boolean] - If true, then the width of the sticky elem will be set as the width of the root element. This can be usefull when the root elem has fluid width and you want sticky elem to have correct width if window is resized. Defaults to true.
 */

export default class ScrollSticker {
    constructor(elem, args) {
        if (args == null) {
            args = {};
        }
        if (!args.hasOwnProperty('target')) {
            return;
        }
        let t = args.target;
        if (typeof t === 'string' || t instanceof String) {
            t = document.querySelector(t);
        }
        if (!t) {
            return;
        }
        const target = t;
        let targetBottom = t;

        const stickyElem = elem.querySelector('[data-scroll-sticker-elem]');
        const stickyParent = stickyElem.closest('.event-page__sticky-sidebar');
        if (!stickyElem) {
            return;
        }

        // Set heading height, to offset sticker
        const firstHeading = target.querySelector('.grouped-section:first-child .grouped-section__headline');
        let startOffsetHeight = 0;
        const groupSection = elem.closest('.grouped-section');
        if (groupSection) {
            elem.style.setProperty('--intro-height', '0px');
        } else {
            if (firstHeading) {
                startOffsetHeight = firstHeading.getBoundingClientRect().height;
            }
            elem.style.setProperty('--intro-height', (startOffsetHeight + 55) + 'px');
        }

        // Options set from arguments
        const snapToTargetBottom = args.hasOwnProperty('snapToTargetBottom') ? args.snapToTargetBottom : true;
        const fixToScreenTop = args.hasOwnProperty('fixToScreenTop') ? args.fixToScreenTop : true;
        const fixToScreenTopOffset = args.hasOwnProperty('fixToScreenTopOffset') ? args.fixToScreenTopOffset : 0;
        const breakpoint = args.hasOwnProperty('breakpoint') ? args.breakpoint : 0;
        const usePositionFixed = args.hasOwnProperty('usePositionFixed') ? args.usePositionFixed : true;
        const throttleDelay = args.hasOwnProperty('throttleDelay') ? args.throttleDelay : 10;
        const saveRootHeight = args.hasOwnProperty('saveRootHeight') ? args.saveRootHeight : true;
        const relateStickyWidthToRoot = args.hasOwnProperty('relateStickyWidthToRoot') ? args.relateStickyWidthToRoot : true;
        const sidebar = elem.closest('.event-page__sidebar');
        const cta = sidebar?.querySelector('.event-page__cta');

        // Inferred variables
        let initialPosition; let initialHeight; let initialTargetBottom; let maxTranslation; let width; let left; let stickyStartScrollPos; let stickyEndScrollPos = 0;
        let isSticky; let applyStickyLogic; let eventHandlerAdded = false;

        const removeSticky = function () {
            stickyElem.classList.remove('cta-sidebar--sticky');
            stickyElem.style.transform = null;
            stickyElem.style.position = null;
            stickyElem.style.width = null;
            stickyElem.style.left = null;
            stickyElem.style.top = null;
            elem.style.height = null;
        };


        function getStickerData(container) {
            const stickerData = container.querySelector('[data-scroll-sticker-elem]');
            if (!stickerData) {
                return null;
            }
            const stickerDataHref = stickerData.href;
            const stickerDataLabel = stickerData.querySelector('.cta-sidebar__btn').innerHTML;
            return (stickerDataHref + stickerDataLabel).toLowerCase();
        }

        function hasSameStickerData(elem1, elem2) {
            return elem1 && elem2 && elem1 == elem2;
        }

        function initMergingContainers() {
            const currentStickerData = getStickerData(groupSection);
            const allSections = [...elem.closest('.umb-block-list').children];
            let i = 0;
            let currentIndex;
            let end;
            let prevStickerData;
            allSections.forEach(x => {
                if (end) {
                    return;
                }

                // if it's the current section,
                if (x == groupSection) {
                    currentIndex = i;
                    // evaluate if it should be disabled
                    if (hasSameStickerData(prevStickerData, currentStickerData)) {
                        stickyElem.classList.add('cta-sidebar--disabled');
                    }
                }

                // if not a section container, skip
                if (!x.classList.contains('grouped-section')) {
                    if (currentIndex || currentIndex === 0) {
                        end = true;
                    }

                    prevStickerData = null;
                    return;
                }

                prevStickerData = getStickerData(x);

                if (currentIndex < i) {
                    // evaluate if there are sections after this one with the same sticker data and set the bottom of the target to the bottom of the last section with the same sticker data
                    if (hasSameStickerData(prevStickerData, currentStickerData)) {
                        targetBottom = x.querySelector('.grouped-section__content-container > .container');
                    } else {
                        end = true;
                    }
                }
                i++;
            });
        }

        if (groupSection && !elem.closest('.event-page')) {
            initMergingContainers();
        }

        const initializeVariables = function () {
            removeSticky();
            isSticky = false;
            // recalculate the initialPosition and targetTop.
            // This is done in the resize event handler to
            // have as little computation as possible in the on scroll handler.
            const stickyElemBR = stickyElem.getBoundingClientRect();

            initialHeight = stickyElemBR.height;
            applyStickyLogic = window.innerWidth >= breakpoint;
            initialPosition = window.scrollY + stickyElemBR.top;

            // if fixToScreenTop is false, stickyStartScrollPos will be 0 which makes to the sticky elem stick immediately on page scroll.
            if (fixToScreenTop) {
                stickyStartScrollPos = initialPosition + fixToScreenTopOffset;
            }

            const targtBR = targetBottom.getBoundingClientRect();
            initialTargetBottom = targtBR.bottom;
            stickyEndScrollPos = window.scrollY + targtBR.top + fixToScreenTopOffset;
            if (snapToTargetBottom) {
                stickyEndScrollPos += targtBR.height - stickyElemBR.height - startOffsetHeight;
            }

            maxTranslation = stickyEndScrollPos - stickyStartScrollPos;
            if (!usePositionFixed) {
                maxTranslation += (stickyElem.getBoundingClientRect().top - elem.getBoundingClientRect().top);
            }

            width = elem.clientWidth;
            left = stickyElemBR.left;
        };

        let scrollDown = true;
        let lastScrollY = 0;
        const determineStickyness = function () {
            scrollDown = lastScrollY < window.scrollY;
            lastScrollY = window.scrollY;
            // check if the size of the element has changed. If so, reinitialize variables
            if ((stickyElem.getBoundingClientRect().height !== initialHeight) || (targetBottom.getBoundingClientRect().bottom !== initialTargetBottom)) {
                initializeVariables();
            }



            if (!applyStickyLogic) {
                removeSticky();
                return;
            }
            let eventCardHeight;
            if (args.eventPageSideBar) {
                eventCardHeight = stickyElem.getBoundingClientRect().height;
                if (eventCardHeight > 300) {
                    stickyParent.style.setProperty('--event-card-height', eventCardHeight + 'px');
                }
            }

            const translate = window.scrollY - stickyStartScrollPos;
            const topOffset = stickyElem.getBoundingClientRect().top - elem.getBoundingClientRect().top;
            const eventCard = elem.querySelector('.event-card');

            if (args.watchScrollSidebar && cta && eventCard && sidebar) {
                if (window.checkCardSpawnedInterval) {
                    window.clearInterval(window.checkCardSpawnedInterval);
                    window.checkCardSpawnedInterval = null;
                }

                const eventHeight = eventCard.getBoundingClientRect().height;
                const ctaHeight = cta.getBoundingClientRect().height;
                const eventCovering = topOffset + translate + eventHeight + 75 - eventCardHeight;
                sidebar.classList.toggle('event-page__sidebar--hide-cta', eventCovering > ctaHeight && window.scrollY > 10);
            }

            if (window.scrollY > stickyEndScrollPos) {
                // sticky destination reached - remove sticky and apply static transform.
                removeSticky();
                stickyElem.classList.add('cta-sidebar--sticky');
                isSticky = false;
                elem.style.height = stickyElem.getBoundingClientRect().height;
                stickyElem.style.transform = 'translateY(' + maxTranslation + 'px)';
            } else if (window.scrollY > stickyStartScrollPos) {
                if (!isSticky) {
                    isSticky = true;

                    if (saveRootHeight) {
                        elem.style.height = stickyElem.getBoundingClientRect().height + 'px';
                    }
                    if (relateStickyWidthToRoot) {
                        stickyElem.style.width = width + 'px';
                    }

                    if (usePositionFixed) {
                        stickyElem.style.transform = null;
                        stickyElem.classList.add('cta-sidebar--sticky');
                        stickyElem.style.position = 'fixed';
                        stickyElem.style.left = left + 'px';
                        stickyElem.style.top = (fixToScreenTopOffset * -1) + 'px';
                    } else {
                        stickyElem.style.transform = null;
                        stickyElem.style.position = 'absolute';
                        stickyElem.style.left = '0px';
                        stickyElem.style.top = topOffset + 'px';
                        stickyElem.style.transform = 'translateY(' + (translate) + 'px)';
                    }
                } else {
                    // If not using fixed position update transform continuously

                    if (!usePositionFixed) {
                        stickyElem.style.transform = 'translateY(' + (translate) + 'px)';
                    }
                }
            } else {
                // Sticky start point not yet reached
                isSticky = false;
                removeSticky();
            }

        };
        const throttledOnScroll = throttle(determineStickyness, throttleDelay, { leading: false });

        const initialize = function () {
            initializeVariables();
            changeContentBasedOnSection();
            if (applyStickyLogic) {
                determineStickyness();
                if (args.watchScrollSidebar) {
                    window.checkCardSpawnedInterval = setInterval(determineStickyness, 100);
                }

                if (!eventHandlerAdded) {
                    eventHandlerAdded = true;
                    window.addEventListener('scroll', throttledOnScroll);
                }
            } else {
                window.removeEventListener('scroll', throttledOnScroll);
                eventHandlerAdded = false;
                removeSticky();
            }
            if (target.querySelectorAll('.grouped-section') !== null && target.querySelectorAll('.grouped-section').length > 1) {
                window.addEventListener('scroll', changeContentBasedOnSection);
                initFirstSectionContent();
            }

            requestAnimationFrame(() => {
                stickyElem.classList.remove('hidden');
            });
        };
        const throttledOnResize = throttle(initialize, 100, { leading: false });

        function initFirstSectionContent() {
            const firstSection = target.querySelector('.grouped-section');
            const settings = JSON.parse(firstSection.dataset.stickySidebarContent);
            const stickyButton = stickyElem.querySelector('[data-sidebar-btn-text]');
            stickyElem.style.backgroundColor = settings.color;
            stickyElem.href = settings.href;
            stickyButton.innerHTML = settings.btnText;
            if (settings.isEnsemble) {
                stickyElem.classList.add('cta-sidebar--logoed');
            } else {
                stickyElem.classList.remove('cta-sidebar--logoed');
            }

            if (settings.size === 'Tall') {
                stickyElem.classList.add('cta-sidebar--tall');
            } else {
                stickyElem.classList.remove('cta-sidebar--tall');
            }
        }

        function loadEnsembleToolbar(settings) {
            function q(attribute, parent = document) {
                return parent.querySelector(`[${attribute}]`);
            }

            const toolbar = document.querySelector('.top-navigation__toolbar__home');
            const image = q('data-image', toolbar);
            const link = q('data-link', toolbar);
            const name = q('data-name', toolbar);

            image.src = settings.ensembleImageUrl;
            link.href = settings.ensembleLink;
            name.innerHTML = settings.ensembleText;
            toolbar.style = `--home-link-bg: ${settings.color}; --home-link-color: #fff`;
        }

        let lastSidebar;
        let timeout;
        let lastBtnText = '';
        let isDefaultToolbar = false;
        const sidebarElem = elem.querySelector('.cta-sidebar');
        const animSpeed = sidebarElem ? getComputedStyle(sidebarElem).getPropertyValue('--anim-speed').replace('ms', '') : '';
        const changeContentBasedOnSection = function () {
            const stickyButton = stickyElem.querySelector('[data-sidebar-btn-text]');
            const sidebarLogo = stickyElem.querySelector('[data-sidebar-logo]');
            let found = false;
            const sidebarContent = target.querySelectorAll('[data-sticky-sidebar-content]');
            if (!lastSidebar && sidebarContent && sidebarContent[0]) {
                lastSidebar = sidebarContent[0];
            }

            sidebarContent.forEach(i => {
                const rectSection = i.getBoundingClientRect();
                if (rectSection.top <= 300 && rectSection.bottom >= 300) {
                    found = true;
                }
                if (rectSection.top <= 300 && rectSection.bottom >= 300 && lastSidebar != i) {
                    lastSidebar = i;
                    const settings = JSON.parse(i.dataset.stickySidebarContent);

                    const fadeOutClass = scrollDown ? 'cta-sidebar--up' : 'cta-sidebar--down';
                    const fadeInClass = scrollDown ? 'cta-sidebar--down' : 'cta-sidebar--up';

                    if (lastBtnText == settings.btnText) {
                        stickyElem.classList.add('cta-sidebar--same-btn-text');
                    } else {
                        stickyElem.classList.remove('cta-sidebar--same-btn-text');
                    }

                    stickyElem.classList.remove('cta-sidebar--no-animate');
                    stickyElem.classList.add(fadeOutClass);

                    if (settings.isEnsemble) {
                        loadEnsembleToolbar(settings);
                        isDefaultToolbar = false;
                    } else if (!applyStickyLogic) {
                        window.loadToolbar();
                        isDefaultToolbar = true;
                        lastSidebar = null;
                    }

                    stickyElem.style.backgroundColor = settings.color;
                    clearTimeout(timeout);
                    timeout = setTimeout(() => {
                        stickyElem.href = settings.href;

                        lastBtnText = settings.btnText;

                        stickyButton.innerHTML = settings.btnText;

                        if (settings.ensembleLogoUrl && sidebarLogo) {
                            sidebarLogo.src = '/';
                            setTimeout(() => {
                                sidebarLogo.src = settings.ensembleLogoUrl;
                                sidebarLogo.classList.toggle('cta-sidebar__logo__ensemble__fp-logo--short', settings.logoInOneLine);
                            }, 1);
                        } else {
                            sidebarLogo.src = '';
                        }

                        if (settings.isEnsemble) {
                            stickyElem.classList.add('cta-sidebar--logoed');
                        } else {
                            stickyElem.classList.remove('cta-sidebar--logoed');
                        }

                        if (settings.size === 'Tall') {
                            stickyElem.classList.add('cta-sidebar--tall');
                        } else {
                            stickyElem.classList.remove('cta-sidebar--tall');
                        }

                        stickyElem.classList.add('cta-sidebar--no-animate');

                        requestAnimationFrame(() => {
                            stickyElem.classList.add(fadeInClass);
                            stickyElem.classList.remove(fadeOutClass);

                            requestAnimationFrame(() => {
                                stickyElem.classList.remove('cta-sidebar--no-animate');

                                requestAnimationFrame(() => {
                                    stickyElem.classList.remove(fadeInClass);
                                    stickyElem.classList.remove(fadeOutClass);
                                });
                            });
                        });
                    }, animSpeed);

                } else {
                    i.classList.remove('active');
                }
            });

            if (!found && !isDefaultToolbar && !applyStickyLogic) {
                window.loadToolbar();
                isDefaultToolbar = true;
                lastSidebar = null;
            }
        };


        initialize();
        window.addEventListener('resize', throttledOnResize);

        this._unload = function () {
            window.removeEventListener('resize', throttledOnResize);

            if (eventHandlerAdded) {
                window.removeEventListener('scroll', throttledOnScroll);
            }
        };
    }

    unload() {
        this._unload();
    }
}
